/**
 * PO Lines Table Component
 * Displays the main data table with headers and rows
 */
import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Button } from '../../../ui/button';
import { Pagination } from '../../../ui/pagination';
import {
  Package,
  Search,
  Loader2,
  Check,
  X,
  Pause,
  Eye,
  EyeOff,
  Download,
  FileDown,
  GripVertical,
  Columns,
  Clock,
  Flame,
} from 'lucide-react';
import { PurchaseOrderLine, ColumnConfig, NextActionResult, POLinesFilters } from './types';
import { getSortIcon } from './utils';
import type { User, POLineExportTemplate } from '../../../../lib/api';
import { POLineRow } from './POLineRow';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';

// CR-3846: Column header configuration for dynamic rendering
interface ColumnHeaderConfig {
  align: 'left' | 'center' | 'right';
  sortable: boolean;
  sortField?: string;
}

// CR-3850: Added additional column headers
const COLUMN_HEADER_CONFIG: Record<string, ColumnHeaderConfig> = {
  processing_stage: { align: 'left', sortable: true, sortField: 'processing_stage' },
  acknowledge_status: { align: 'left', sortable: true, sortField: 'acknowledge_status' },
  action: { align: 'center', sortable: false },
  is_critical: { align: 'center', sortable: true, sortField: 'is_critical' },
  on_hold: { align: 'center', sortable: true, sortField: 'on_hold' },
  hold_reason: { align: 'left', sortable: true, sortField: 'hold_reason' },
  next_action: { align: 'center', sortable: true, sortField: 'next_action' },
  po_number: { align: 'left', sortable: true, sortField: 'po_number' },
  line_number: { align: 'center', sortable: true, sortField: 'line_number' },
  supplier_name: { align: 'left', sortable: true, sortField: 'supplier_name' },
  customer_name: { align: 'left', sortable: true, sortField: 'customer_name' },
  customer_group_name: { align: 'left', sortable: true, sortField: 'customer_group_name' },
  part_number: { align: 'left', sortable: true, sortField: 'part_number' },
  description: { align: 'left', sortable: true, sortField: 'description' },
  quantity: { align: 'right', sortable: true, sortField: 'quantity' },
  received_qty: { align: 'right', sortable: true, sortField: 'received_qty' },
  outstanding_qty: { align: 'right', sortable: true, sortField: 'outstanding_qty' },
  pct_delivered: { align: 'center', sortable: true, sortField: 'pct_delivered' },
  unit_price: { align: 'right', sortable: true, sortField: 'unit_price' },
  total_price: { align: 'right', sortable: true, sortField: 'total_price' },
  currency: { align: 'center', sortable: true, sortField: 'currency' },
  delivery_date: { align: 'left', sortable: true, sortField: 'delivery_date' },
  supplier_promise_date: { align: 'left', sortable: true, sortField: 'supplier_promise_date' },
  next_job_due: { align: 'left', sortable: true, sortField: 'next_job_due' },
  buyer_id: { align: 'left', sortable: true, sortField: 'buyer_id' },
  batch_serial_no: { align: 'left', sortable: true, sortField: 'batch_serial_no' },
  ppv: { align: 'right', sortable: true, sortField: 'ppv' },
  otif_overridden: { align: 'center', sortable: true, sortField: 'otif_overridden' },
  packing_note_number: { align: 'left', sortable: true, sortField: 'packing_note_number' },
  order_date: { align: 'left', sortable: true, sortField: 'order_date' },
  last_received_date: { align: 'left', sortable: true, sortField: 'last_received_date' },
  updated_at: { align: 'left', sortable: true, sortField: 'updated_at' },
  required_date_change_count: { align: 'center', sortable: true, sortField: 'required_date_change_count' },
  promised_date_change_count: { align: 'center', sortable: true, sortField: 'promised_date_change_count' },
  pdf_attached_count: { align: 'center', sortable: true, sortField: 'pdf_attached_count' },
  material_cert_count: { align: 'center', sortable: true, sortField: 'material_cert_count' },
  contract_unit_price: { align: 'right', sortable: true, sortField: 'contract_unit_price' },
  contract_lot_price: { align: 'right', sortable: true, sortField: 'contract_lot_price' },
  drawing_number: { align: 'left', sortable: true, sortField: 'drawing_number' },
  drawing_version: { align: 'left', sortable: true, sortField: 'drawing_version' },
  outstanding_value: { align: 'right', sortable: true, sortField: 'outstanding_value' },
  converted_unit_price: { align: 'right', sortable: true, sortField: 'converted_unit_price' },
  converted_total_value: { align: 'right', sortable: true, sortField: 'converted_total_value' },
  converted_outstanding_value: { align: 'right', sortable: true, sortField: 'converted_outstanding_value' },
  original_promise_date: { align: 'left', sortable: true, sortField: 'original_promise_date' },
  mrp_scheduled_date: { align: 'left', sortable: true, sortField: 'mrp_scheduled_date' },
  priority: { align: 'center', sortable: true, sortField: 'priority' },
  contract_price: { align: 'right', sortable: true, sortField: 'contract_price' },
  exchange_rate: { align: 'right', sortable: true, sortField: 'exchange_rate' },  // CR-4679
  // Poeton ProgressLog columns — sourced from line.latest_progress on the
  // backend, not direct PurchaseOrderLine fields, so they are not sortable
  // by a simple ORDER BY. Without these entries the <th> is null while the
  // <td> still renders, misaligning every column to the right of them.
  job_status: { align: 'left', sortable: false },
  job_no_active: { align: 'left', sortable: false },
  job_qty_active: { align: 'right', sortable: false },
  poeton_job_number: { align: 'left', sortable: true, sortField: 'poeton_job_number' },  // CR-5260
  otd_overridden: { align: 'center', sortable: true, sortField: 'otd_overridden' },  // CR-5263
};

interface POLinesTableProps {
  lines: PurchaseOrderLine[];
  totalCount: number;
  needsActionCount: number | null;
  openCount: number | null;
  closedCount: number | null;
  onHoldCount: number | null;
  completedCount: number | null;
  dueTodayCount: number | null;
  highPriorityCount: number | null;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  activeTab: string;
  showFilters: boolean;
  exporting: boolean;
  user: User | null;
  columns: ColumnConfig[];
  showColumnDropdown: boolean;
  columnDropdownRef: React.RefObject<HTMLDivElement | null>;
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  selectedLineIds: Set<number>;
  bulkAccepting: boolean;
  bulkRejecting: boolean;
  bulkHolding: boolean;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  formatCurrency: (value: number, currency?: string, showDecimals?: boolean | number) => string;
  onSetActiveTab: (tab: string) => void;
  onSetShowFilters: (show: boolean) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSort: (field: string) => void;
  onExport: (format: 'xlsx' | 'csv') => void;
  onTemplateExport: (templateId: number, format: 'xlsx' | 'csv') => void;
  exportTemplates: POLineExportTemplate[];
  onToggleColumn: (columnId: string) => void;
  onResetColumns: () => void;
  onSelectAllColumns: () => void;
  onReorderColumns: (dragIndex: number, hoverIndex: number) => void;
  onSetShowColumnDropdown: (show: boolean) => void;
  onClearFilters: () => void;
  onSelectLine: (lineId: number) => void;
  onReviewChanges: (lineId: number) => void;  // CR-4728
  onShowJobStatus: (lineId: number) => void;
  onSelectLineForWorkflow: (line: PurchaseOrderLine, modal: 'accept' | 'decline') => void;
  onProposeChange: (line: PurchaseOrderLine) => void;
  onToggleCritical: (line: PurchaseOrderLine) => void;
  onPutOnHold: (line: PurchaseOrderLine) => void;
  onReleaseHold: (line: PurchaseOrderLine) => void;
  onSelectAll: () => void;
  onToggleSelect: (lineId: number) => void;
  onBulkAccept: () => void;
  onBulkReject: () => void;
  onBulkHold: () => void;
  onSetSelectedLineIds: (ids: Set<number>) => void;
  shouldShowActions: (line: PurchaseOrderLine) => boolean;
  shouldShowDecline: (line: PurchaseOrderLine) => boolean;
  shouldShowProposeChange: (line: PurchaseOrderLine) => boolean;
  getNextAction: (line: PurchaseOrderLine) => NextActionResult;
  getSelectableLines: () => PurchaseOrderLine[];
  canSelectForBulkAccept: (line: PurchaseOrderLine) => boolean;
  filters: POLinesFilters;
}

// Columns hidden in the PO Lines table when schedule_collaboration is enabled
// (these are managed at the schedule level instead).
const SCHEDULE_COLLAB_HIDDEN_TABLE_COLUMNS = new Set([
  'acknowledge_status',
  'action',
  'next_action',
  'on_hold',
  'hold_reason',
  'supplier_promise_date',
]);

export function POLinesTable({
  lines, totalCount, openCount, onHoldCount, completedCount, dueTodayCount, highPriorityCount, currentPage, totalPages, pageSize,
  activeTab, showFilters, exporting, user, columns, showColumnDropdown,
  columnDropdownRef, sortField, sortDirection, selectedLineIds,
  bulkAccepting, bulkRejecting, bulkHolding,
  formatDate, formatCurrency,
  onSetActiveTab, onSetShowFilters, onPageChange, onPageSizeChange, onSort, onExport,
  onTemplateExport, exportTemplates,
  onToggleColumn, onResetColumns, onSelectAllColumns, onReorderColumns,
  onSetShowColumnDropdown, onClearFilters, onSelectLine, onReviewChanges, onShowJobStatus, onSelectLineForWorkflow,
  onProposeChange, onToggleCritical, onPutOnHold, onReleaseHold,
  onSelectAll, onToggleSelect, onBulkAccept, onBulkReject, onBulkHold,
  onSetSelectedLineIds,
  shouldShowActions, shouldShowDecline, shouldShowProposeChange,
  getNextAction, getSelectableLines, canSelectForBulkAccept, filters,
}: POLinesTableProps) {
  // When schedule_collaboration is enabled, the PO Lines tab becomes a read-only
  // order-data view. Strip collab columns from the visible set even if the user
  // had toggled them on previously.
  const { isFeatureEnabled } = useMenuSettings();
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');
  const visibleColumns = scheduleCollabEnabled
    ? columns.filter(col => !SCHEDULE_COLLAB_HIDDEN_TABLE_COLUMNS.has(col.id))
    : columns;
  // CR-4687 round 3: ≥2 rows selected = bulk mode. Per-line action buttons in
  // each row render disabled while this is true; the only allowed actions are
  // the bulk buttons in the header bar (or clearing the selection).
  const bulkMode = selectedLineIds.size >= 2;

  // "Download Report" menu — lets the user pick a saved export template and
  // download it (Excel/CSV). Self-contained local state with click-outside.
  const [showReportMenu, setShowReportMenu] = useState(false);
  // Report flow: pick a template -> preview its columns -> Download (Excel).
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const reportMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showReportMenu) { setSelectedTemplateId(null); return; }
    const handler = (e: MouseEvent) => {
      if (reportMenuRef.current && !reportMenuRef.current.contains(e.target as Node)) {
        setShowReportMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showReportMenu]);
  const selectedTemplate = exportTemplates.find((t) => t.id === selectedTemplateId) ?? null;
  // Humanise a column key (snake_case) into a readable preview label.
  const humanizeColumn = (key: string): string =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Card data-tour="po-lines-table">
      <Tabs value={activeTab} onValueChange={onSetActiveTab}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>SO Line Items</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Showing {lines.length} of {totalCount.toLocaleString()} line items
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => onSetShowFilters(!showFilters)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                <Search className="h-3 w-3" />Filters
              </button>
              <button onClick={() => onExport('xlsx')} disabled={exporting || lines.length === 0} className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed" title="Export filtered data to Excel">
                {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}Export
              </button>
              {/* Download Report — pick a saved export template, preview its
                  columns, then download as Excel. */}
              <div className="relative" ref={reportMenuRef}>
                <button
                  onClick={() => setShowReportMenu((v) => !v)}
                  disabled={exporting || lines.length === 0}
                  className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${showReportMenu ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  title="Download a saved report template"
                  aria-label="Download a saved report template"
                  aria-haspopup="menu"
                  aria-expanded={showReportMenu}
                >
                  <FileDown className="h-3 w-3" />Download Report
                </button>
                {showReportMenu && (
                  <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-border rounded-lg shadow-lg z-50 flex flex-col max-h-[460px]">
                    <div className="p-2 border-b text-sm font-medium shrink-0">Saved report templates</div>
                    {exportTemplates.length === 0 ? (
                      <div className="p-3 text-xs text-muted-foreground">
                        No report templates yet. Create one in <span className="font-medium">Admin Setup → Purchasing → Export Templates</span>, then it will appear here.
                      </div>
                    ) : (
                      <>
                        {/* Step 1: select a template */}
                        <div className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: '160px' }}>
                          {exportTemplates.map((tpl) => (
                            <button
                              key={tpl.id}
                              type="button"
                              onClick={() => setSelectedTemplateId(tpl.id)}
                              className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-left ${selectedTemplateId === tpl.id ? 'bg-primary/10 ring-1 ring-primary' : 'hover:bg-muted/50'}`}
                              title={tpl.name}
                            >
                              <span className="text-sm truncate">{tpl.name}</span>
                              <span className="text-xs text-muted-foreground shrink-0">{tpl.column_count} cols</span>
                            </button>
                          ))}
                        </div>
                        {/* Step 2: preview the selected template's columns */}
                        {selectedTemplate && (
                          <div className="border-t p-2 overflow-y-auto" style={{ maxHeight: '180px' }}>
                            <div className="text-xs font-medium text-muted-foreground mb-1.5">
                              Columns in “{selectedTemplate.name}” ({selectedTemplate.columns.length})
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {selectedTemplate.columns.map((col, idx) => (
                                <span key={`${col}-${idx}`} className="text-xs px-1.5 py-0.5 rounded bg-muted">
                                  {humanizeColumn(col)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Step 3: download (Excel) */}
                        <div className="border-t p-2 shrink-0">
                          <Button
                            type="button"
                            size="sm"
                            className="w-full"
                            disabled={!selectedTemplate || exporting}
                            onClick={() => {
                              if (!selectedTemplate) return;
                              onTemplateExport(selectedTemplate.id, 'xlsx');
                              setShowReportMenu(false);
                            }}
                          >
                            <Download className="h-4 w-4 mr-1.5" />
                            {selectedTemplate ? `Download “${selectedTemplate.name}” (Excel)` : 'Select a report to download'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Column Visibility Dropdown */}
              <div className="relative" ref={columnDropdownRef}>
                <button onClick={() => onSetShowColumnDropdown(!showColumnDropdown)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showColumnDropdown ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                  <Columns className="h-3 w-3" />Columns
                </button>
                {showColumnDropdown && (
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
                    <div className="p-2 border-b flex items-center justify-between">
                      <span className="text-sm font-medium">Manage Columns</span>
                      <div className="flex items-center gap-2">
                        <button onClick={onSelectAllColumns} className="text-xs text-primary hover:underline">Select All</button>
                        <span className="text-xs text-muted-foreground">|</span>
                        <button onClick={onResetColumns} className="text-xs text-primary hover:underline">Reset</button>
                      </div>
                    </div>
                    <div className="px-2 py-1 text-xs text-muted-foreground border-b bg-muted/30">Drag to reorder - Click to show/hide</div>
                    <div className="p-2 space-y-1">
                      {columns.map((col, index) => (
                        <div
                          key={col.id}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData('text/plain', index.toString()); e.currentTarget.classList.add('opacity-50'); }}
                          onDragEnd={(e) => { e.currentTarget.classList.remove('opacity-50'); }}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-primary/10'); }}
                          onDragLeave={(e) => { e.currentTarget.classList.remove('bg-primary/10'); }}
                          onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-primary/10'); const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10); if (dragIndex !== index) onReorderColumns(dragIndex, index); }}
                          className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-move group"
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
                          <input type="checkbox" checked={col.visible} onChange={() => onToggleColumn(col.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer" />
                          <span className="text-sm flex-1">{col.label}</span>
                          {col.visible ? <Eye className="h-3 w-3 text-muted-foreground" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <TabsList data-tour="quick-filters" className="mt-3 justify-start">
            <TabsTrigger value="all">All Lines</TabsTrigger>
            <TabsTrigger value="open">Open{openCount !== null && openCount > 0 ? ` (${openCount.toLocaleString()})` : ''}</TabsTrigger>
            {!scheduleCollabEnabled && (
              <TabsTrigger value="on_hold" data-tour="on-hold-filter">On Hold{onHoldCount !== null && onHoldCount > 0 ? ` (${onHoldCount.toLocaleString()})` : ''}</TabsTrigger>
            )}
            <TabsTrigger value="completed">Completed{completedCount !== null && completedCount > 0 ? ` (${completedCount.toLocaleString()})` : ''}</TabsTrigger>
            {/* Quick-filter views: open lines due today (by ODD) / flagged high priority. */}
            <TabsTrigger value="due_today" className="gap-1.5"><Clock className="h-4 w-4" />Due Today{dueTodayCount !== null && dueTodayCount > 0 ? ` (${dueTodayCount.toLocaleString()})` : ''}</TabsTrigger>
            <TabsTrigger value="high_priority" className="gap-1.5"><Flame className="h-4 w-4" />High Priority{highPriorityCount !== null && highPriorityCount > 0 ? ` (${highPriorityCount.toLocaleString()})` : ''}</TabsTrigger>
          </TabsList>
        </CardHeader>

        <TabsContent value={activeTab} className="m-0">
          <CardContent>
            {lines.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{Object.values(filters).some(v => v) ? 'No lines match your filters' : 'No line items found'}</p>
                {Object.values(filters).some(v => v) && (<button onClick={onClearFilters} className="mt-4 text-sm text-primary hover:underline">Clear filters</button>)}
              </div>
            ) : (
              <>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={pageSize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} itemLabel="lines" />

                {/* Bulk Action Bar — supplier-portal hides bulk Accept / Hold / Reject
                    product-wide (collaboration features are not part of the supplier
                    portal contract). Previously gated only by scheduleCollabEnabled.
                    See sibling: POLineFooter (hides per-line collaboration buttons,
                    commit 5c6d2b40). */}
                {false && !scheduleCollabEnabled && (
                <div className={`mb-4 p-3 border rounded-lg flex items-center gap-3 ${bulkMode ? 'bg-amber-50 border-amber-300' : 'bg-muted/40 border-border'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${bulkMode ? 'text-amber-900 font-medium' : 'text-muted-foreground'}`}>
                      {selectedLineIds.size > 0 ? `${selectedLineIds.size} line${selectedLineIds.size > 1 ? 's' : ''} selected` : 'No lines selected'}
                    </span>
                    {/* CR-4687 round 3: explicit bulk-mode indicator so testers know per-line buttons are intentionally disabled. */}
                    {bulkMode && (
                      <span className="text-xs text-amber-800">
                        — bulk mode: per-line actions disabled. Use the buttons here, or clear the selection.
                      </span>
                    )}
                    {selectedLineIds.size > 0 && (<button onClick={() => onSetSelectedLineIds(new Set())} className="text-xs text-muted-foreground underline hover:text-foreground">Clear</button>)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={onBulkHold} disabled={selectedLineIds.size === 0 || bulkHolding} className={selectedLineIds.size > 0 ? 'border-orange-500 text-orange-700 hover:bg-orange-50' : 'opacity-60 cursor-not-allowed'}>
                      {bulkHolding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Holding...</> : <><Pause className="h-4 w-4 mr-2" />Put On Hold All</>}
                    </Button>
                    <Button size="sm" onClick={onBulkAccept} disabled={selectedLineIds.size === 0 || bulkAccepting} className={selectedLineIds.size > 0 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-200 text-green-600 cursor-not-allowed opacity-60'}>
                      {bulkAccepting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Accepting...</> : <><Check className="h-4 w-4 mr-2" />Accept All</>}
                    </Button>
                    <Button size="sm" onClick={onBulkReject} disabled={selectedLineIds.size === 0 || bulkRejecting} className={selectedLineIds.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-200 text-red-400 cursor-not-allowed opacity-60'}>
                      {bulkRejecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Rejecting...</> : <><X className="h-4 w-4 mr-2" />Reject All</>}
                    </Button>
                  </div>
                </div>
                )}

                <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
                  <table className="w-full">
                    <thead className="sticky top-0 z-20">
                      <tr className="border-b bg-muted/80 backdrop-blur-sm">
                        {/* Select-all checkbox column hidden product-wide on supplier-portal */}
                        {false && !scheduleCollabEnabled && (
                          <th className="text-center py-3 px-2 font-medium" style={{ minWidth: '50px' }}>
                            <input type="checkbox" checked={getSelectableLines().length > 0 && selectedLineIds.size === getSelectableLines().length} onChange={onSelectAll} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" title="Select all eligible lines" />
                          </th>
                        )}
                        {visibleColumns.filter(col => col.visible).map(col => {
                          const config = COLUMN_HEADER_CONFIG[col.id];
                          if (!config) return null;
                          const alignClass = config.align === 'center' ? 'text-center' : config.align === 'right' ? 'text-right' : 'text-left';
                          const justifyClass = config.align === 'center' ? 'justify-center' : config.align === 'right' ? 'justify-end' : '';
                          return (
                            <th key={col.id} className={`${alignClass} py-3 px-4 font-medium ${config.sortable ? 'cursor-pointer hover:bg-muted/50' : ''}`} style={{ minWidth: col.minWidth }} onClick={config.sortable && config.sortField ? () => onSort(config.sortField!) : undefined}>
                              {config.sortable ? (<div className={`flex items-center gap-1 ${justifyClass}`}>{col.label} {getSortIcon(config.sortField!, sortField, sortDirection)}</div>) : col.label}
                            </th>
                          );
                        })}
                        {/* Supplier-portal: trailing 'View →' column removed —
                            row click already opens detail; the empty-then-link
                            cell offset the table by 80px on every row. */}
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <POLineRow
                          key={line.id} line={line} user={user} columns={visibleColumns}
                          scheduleCollabEnabled={scheduleCollabEnabled} bulkMode={bulkMode}
                          selectedLineIds={selectedLineIds} formatDate={formatDate} formatCurrency={formatCurrency}
                          shouldShowActions={shouldShowActions} shouldShowDecline={shouldShowDecline}
                          shouldShowProposeChange={shouldShowProposeChange} getNextAction={getNextAction}
                          canSelectForBulkAccept={canSelectForBulkAccept} onSelectLine={onSelectLine}
                          onReviewChanges={onReviewChanges}
                          onShowJobStatus={onShowJobStatus}
                          onSelectLineForWorkflow={onSelectLineForWorkflow} onProposeChange={onProposeChange}
                          onToggleCritical={onToggleCritical} onPutOnHold={onPutOnHold}
                          onReleaseHold={onReleaseHold} onToggleSelect={onToggleSelect}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={pageSize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} itemLabel="lines" />
              </>
            )}
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
